(() => {
  if (customElements.get('stamatis-room-studio')) return;

  class StamatisRoomStudio extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.items = [];
      this.products = new Map();
      this.photoUrl = '';
      this.roomFile = null;
      this.generating = false;
      this.serviceReady = false;
      this.uncertain = false;
      this.requestId = '';
      this.photoVersion = 0;
      this.events = new AbortController();
      this.rootPath = this.dataset.root || '/';
      if (!this.rootPath.endsWith('/')) this.rootPath += '/';
      this.maxProducts = 4;
      this.proxyBase = (this.dataset.proxyBase || `${this.rootPath}apps/room-preview`).replace(/\/$/, '');
      try {
        this.visitorId = localStorage.getItem('srp_visitor') || crypto.randomUUID();
        localStorage.setItem('srp_visitor', this.visitorId);
        this.emailToken = localStorage.getItem('srp_email_token') || '';
      } catch { this.visitorId = crypto.randomUUID(); this.emailToken = ''; }
      this.copy = this.readJson('[data-studio-copy]', {});
      this.seeds = this.readJson('[data-seed-products]', []);
      this.results = this.seeds;
      this.categories = this.readJson('[data-catalog-categories]', []);
      this.currency = this.dataset.currency || 'EUR';
      this.locale = this.dataset.language || 'el';
      this.dialog = this.querySelector('[data-catalog]');
      this.money = new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency });
      this.bindEvents();
      this.initDictation();
      this.setBriefMode('written');
      this.renderSelection();
      this.updateSummary();
      this.connectService();
    }

    disconnectedCallback() {
      this.cancelDictation();
      this.events?.abort();
      this.searchRequest?.abort();
      this.productRequest?.abort();
      clearTimeout(this.searchTimer);
      if (this.photoUrl && this.photoUrl !== this.resultPhotoUrl) URL.revokeObjectURL(this.photoUrl);
      if (this.resultPhotoUrl) URL.revokeObjectURL(this.resultPhotoUrl);
      this.dialog?.close();
      this.initialized = false;
    }

    readJson(selector, fallback) {
      try { return JSON.parse(this.querySelector(selector)?.textContent || ''); }
      catch { return fallback; }
    }

    t(key, values = {}) {
      return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`{${name}}`, String(value)), this.copy[key] || key);
    }

    escape(value) {
      return String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
    }

    imageUrl(value) {
      const source = typeof value === 'string' ? value : value?.url || value?.src;
      if (!source) return '';
      try {
        const url = new URL(source, location.origin);
        if (url.protocol !== 'https:' || !(url.hostname === location.hostname || url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.cdn.shopify.com'))) return '';
        url.searchParams.set('width', '480');
        return url.href;
      } catch { return ''; }
    }

    on(target, event, handler) {
      target.addEventListener(event, handler, { signal: this.events.signal });
    }

    bindEvents() {
      this.on(this, 'click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-generate')) this.generate();
        if (button.hasAttribute('data-download')) this.downloadResult();
        if (button.hasAttribute('data-edit-composition')) {
          this.setBriefMode('written');
          this.querySelector('[data-brief]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          this.querySelector('[data-brief]')?.focus({ preventScroll: true });
        }
        if (button.hasAttribute('data-retry-connection')) this.connectService(true);
        if (this.generating) return;
        if (button.hasAttribute('data-brief-mode')) this.setBriefMode(button.dataset.briefMode);
        if (button.hasAttribute('data-dictate')) this.toggleDictation();
        if (button.hasAttribute('data-open-catalog')) this.openCatalog(button);
        if (button.hasAttribute('data-close-catalog')) this.dialog.close();
        if (button.hasAttribute('data-choose-photo') || button.hasAttribute('data-replace-photo')) this.querySelector('[data-file]').click();
        if (button.hasAttribute('data-take-photo')) this.querySelector('[data-camera-file]').click();
        if (button.hasAttribute('data-clear-photo')) this.clearPhoto();
        if (button.dataset.remove) this.removeProduct(button.dataset.remove);
        if (button.dataset.add) this.addProduct(button.dataset.add, button);
        if (button.hasAttribute('data-category')) {
          const input = this.querySelector('[data-search]');
          input.value = '';
          this.search('', button.dataset.category);
        }
      });
      this.on(this, 'change', event => {
        if (event.target.matches('[data-file], [data-camera-file]')) {
          const file = event.target.files?.[0];
          if (file) this.loadPhoto(file);
          event.target.value = '';
        }
        const item = this.items.find(product => product.handle === event.target.dataset.handle);
        if (item && event.target.matches('[data-variant]')) {
          const variant = item.variants.find(candidate => String(candidate.id) === event.target.value && candidate.available);
          if (variant) item.variant = variant;
          this.renderSelection();
          this.querySelector(`[data-variant][data-handle="${item.handle}"]`)?.focus();
        }
        this.updateSummary();
      });
      this.on(this, 'input', event => {
        if (event.target.matches('[data-brief]')) this.updateSummary();
        if (event.target.matches('[data-compare]')) this.querySelector('[data-comparison]').style.setProperty('--split', `${event.target.value}%`);
      });
      this.on(this, 'keydown', event => {
        if (!event.target.matches('[data-brief-mode]') || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const mode = event.key === 'Home' ? 'written' : event.key === 'End' ? 'spoken' : this.briefMode === 'spoken' ? 'written' : 'spoken';
        if (this.setBriefMode(mode)) this.querySelector(`[data-brief-mode="${mode}"]`).focus();
      });
      this.on(this.querySelector('[data-search]'), 'input', event => {
        clearTimeout(this.searchTimer);
        this.searchRequest?.abort();
        this.searchTimer = setTimeout(() => this.search(event.target.value), 300);
      });
      this.on(this.dialog, 'close', () => this.dialogOpener?.focus());
      const drop = this.querySelector('[data-drop]');
      this.on(drop, 'dragover', event => { event.preventDefault(); drop.classList.add('is-dragging'); });
      this.on(drop, 'dragleave', event => { if (!drop.contains(event.relatedTarget)) drop.classList.remove('is-dragging'); });
      this.on(drop, 'drop', event => {
        event.preventDefault();
        drop.classList.remove('is-dragging');
        const files = event.dataTransfer?.files;
        if (files?.length !== 1) this.status(this.t('one_photo'));
        else this.loadPhoto(files[0]);
      });
    }

    status(message) { this.querySelector('[data-status]').textContent = message; }

    async loadPhoto(file) {
      const version = ++this.photoVersion;
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || !file.size || file.size > 8 * 1024 * 1024) {
        this.status(this.t('invalid_photo'));
        return;
      }
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.src = url;
      try {
        await image.decode();
        if (version !== this.photoVersion || !this.isConnected) { URL.revokeObjectURL(url); return; }
        if (!image.naturalWidth || image.naturalWidth * image.naturalHeight > 32000000) throw new Error('Photo pixel limit');
        if (this.photoUrl && this.photoUrl !== this.resultPhotoUrl) URL.revokeObjectURL(this.photoUrl);
        this.photoUrl = url;
        this.roomFile = file;
        this.querySelector('[data-room-image]').src = url;
        this.querySelector('[data-room-image]').hidden = false;
        this.querySelector('[data-upload-empty]').hidden = true;
        this.querySelector('[data-image-label]').hidden = false;
        this.querySelector('[data-photo-tools]').hidden = false;
        const filename = this.querySelector('[data-photo-name]');
        filename.textContent = file.name;
        filename.classList.add('srs-photo-name');
        this.status(this.t('photo_ready'));
        window.StamatisStudioAnalytics?.track('photo_uploaded', this.items.map(item=>item.handle));
        this.updateSummary();
      } catch {
        URL.revokeObjectURL(url);
        if (version === this.photoVersion) this.status(this.t('unreadable_photo'));
      }
    }

    clearPhoto() {
      this.photoVersion++;
      if (this.photoUrl && this.photoUrl !== this.resultPhotoUrl) URL.revokeObjectURL(this.photoUrl);
      this.photoUrl = '';
      this.roomFile = null;
      const image = this.querySelector('[data-room-image]');
      image.hidden = true;
      image.removeAttribute('src');
      this.querySelector('[data-upload-empty]').hidden = false;
      this.querySelector('[data-image-label]').hidden = true;
      this.querySelector('[data-photo-tools]').hidden = true;
      const filename = this.querySelector('[data-photo-name]');
      filename.textContent = this.t('photo_step');
      filename.classList.remove('srs-photo-name');
      this.status(this.t('photo_removed'));
      this.updateSummary();
      this.querySelector('[data-choose-photo]').focus();
    }

    openCatalog(opener) {
      this.dialogOpener = opener;
      this.querySelector('[data-catalog-status]').textContent = '';
      this.renderCatalog();
      this.dialog.showModal();
      if (!this.catalogProducts && !this.querySelector('[data-search]').value) this.search('', this.activeCategory || '');
      if (matchMedia('(min-width: 750px)').matches) this.querySelector('[data-search]').focus();
    }

    normalizeProduct(product) {
      let handle = product.handle;
      if (!handle && product.url) {
        try { handle = new URL(product.url, location.origin).pathname.match(/\/products\/([^/]+)/)?.[1]; }
        catch { return null; }
      }
      if (!handle || !/^[a-z0-9][a-z0-9-]*$/.test(handle)) return null;
      const image = this.imageUrl(product.image || product.featured_image);
      if (!image || !product.title) return null;
      const price = typeof product.price === 'string' ? Math.round(Number(product.price) * 100) : Number(product.price);
      return { handle, title: String(product.title), image, price: Number.isFinite(price) ? price : null, available: product.available !== false };
    }

    foldSearch(value) {
      return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('el').replace(/ς/g, 'σ');
    }

    searchTerms(query) {
      const stopwords = new Set(['με', 'σε', 'και', 'το', 'τα', 'την', 'τη', 'τον', 'του', 'των', 'ενα', 'μια', 'για', 'απο', 'χωρο', 'χωροσ']);
      return this.foldSearch(query).replace(/[^\p{L}\p{N}]+/gu, ' ').split(/\s+/).filter(word => word && !stopwords.has(word)).map(word => {
        if (/^κρεβατ/.test(word)) return ['κρεβατ', 'bed'];
        if (/^καναπ/.test(word) || /^σαλον/.test(word)) return ['καναπ', 'σαλον', 'sofa'];
        if (/^φωτισ/.test(word)) return ['φωτισ', 'πορτατιφ', 'απλικ', 'φωτ '];
        if (/^καρεκλ/.test(word)) return ['καρεκλ', 'chair'];
        if (/^τραπεζ/.test(word)) return ['τραπεζ', 'table'];
        if (/^αποθηκευ/.test(word)) return ['αποθηκευτ', 'αποθηκευσ', 'συρταρ', 'storage', 'μπαουλο'];
        if (/^ροζ/.test(word) || word === 'pink') return ['ροζ', 'pink'];
        if (/^γκρι/.test(word) || /^(grey|gray)$/.test(word)) return ['γκρι', 'grey', 'gray'];
        if (/^λευκ/.test(word) || /^ασπρ/.test(word) || word === 'white') return ['λευκ', 'ασπρ', 'white'];
        if (/^μαυρ/.test(word) || word === 'black') return ['μαυρ', 'black'];
        if (/^μπεζ/.test(word) || word === 'beige') return ['μπεζ', 'beige'];
        if (/^πρασιν/.test(word) || word === 'green') return ['πρασιν', 'green'];
        if (/^κοκκιν/.test(word) || word === 'red') return ['κοκκιν', 'red'];
        if (word === 'μπλε' || word === 'blue') return ['μπλε', 'blue'];
        if (word === 'καφε' || word === 'brown') return ['καφε', 'brown'];
        if (word === 'εκρου' || word === 'ecru') return ['εκρου', 'ecru'];
        return [word];
      });
    }

    categoryProducts(products, category) {
      const definition = this.categories.find(item => item.key === category);
      if (!definition) return products;
      return products.filter(product => definition.types.some(type => this.foldSearch(product.type).includes(type)));
    }

    async loadCatalog() {
      if (this.catalogProducts) return this.catalogProducts;
      if (this.catalogPromise) return this.catalogPromise;
      this.catalogPromise = (async () => {
        const products = [];
        const seen = new Set();
        let page = 1;
        do {
          const url = new URL(`${this.rootPath}collections/all`, location.origin);
          url.searchParams.set('sort_by', 'best-selling');
          url.searchParams.set('section_id', 'ai-room-studio-catalog');
          url.searchParams.set('page', String(page));
          const response = await fetch(url, { signal: this.events.signal, headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Catalog unavailable');
          const document = new DOMParser().parseFromString(await response.text(), 'text/html');
          const payload = JSON.parse(document.querySelector('[data-studio-catalog-page]')?.textContent || 'null');
          if (!payload || !Array.isArray(payload.products)) throw new Error('Invalid catalog');
          for (const product of payload.products) {
            if (seen.has(product.handle) || !this.normalizeProduct(product)) continue;
            seen.add(product.handle);
            product.searchText = this.foldSearch([product.title, product.type, product.details].join(' '));
            products.push(product);
          }
          const nextPage = Number(payload.nextPage);
          if (nextPage && nextPage <= page) throw new Error('Invalid catalog pagination');
          page = nextPage;
        } while (page);
        this.catalogProducts = products;
        return products;
      })();
      try { return await this.catalogPromise; }
      finally { this.catalogPromise = null; }
    }

    catalogSuggestions(products) {
      const groups = this.categories.map(category => this.categoryProducts(products, category.key));
      const suggestions = [];
      const seen = new Set();
      for (let index = 0; index < 20 && suggestions.length < 20; index++) {
        for (const group of groups) {
          const product = group[index];
          if (product && !seen.has(product.handle)) {
            suggestions.push(product);
            seen.add(product.handle);
          }
          if (suggestions.length === 20) break;
        }
      }
      return suggestions;
    }

    async search(term, category = '') {
      const query = term.trim().slice(0, 120);
      this.activeCategory = category;
      clearTimeout(this.searchTimer);
      this.searchRequest?.abort();
      this.searchRequest = new AbortController();
      this.querySelectorAll('[data-category]').forEach(button => button.setAttribute('aria-pressed', String(!query && button.dataset.category === category)));
      const signal = this.searchRequest.signal;
      this.querySelector('[data-catalog-status]').textContent = this.t('searching');
      this.querySelector('[data-catalog-grid]').setAttribute('aria-busy', 'true');
      this.querySelector('[data-catalog-grid]').innerHTML = `<p class="srs-catalog-empty">${this.escape(this.t('searching'))}</p>`;
      try {
        const products = await this.loadCatalog();
        if (signal.aborted || !this.isConnected) return;
        const terms = this.searchTerms(query);
        this.results = query
          ? products.filter(product => terms.every(alternatives => alternatives.some(word => product.searchText.includes(word)))).slice(0, 60)
          : category ? this.categoryProducts(products, category).slice(0, 20) : this.catalogSuggestions(products);
        this.querySelector('[data-catalog-status]').textContent = query ? this.t('search_results') : '';
        this.renderCatalog();
      } catch (error) {
        if (error.name !== 'AbortError' && !signal.aborted) {
          this.results = [];
          this.renderCatalog();
          this.querySelector('[data-catalog-status]').textContent = this.t('search_failed');
        }
      } finally {
        if (!signal.aborted) this.querySelector('[data-catalog-grid]').setAttribute('aria-busy', 'false');
      }
    }

    renderCatalog() {
      const grid = this.querySelector('[data-catalog-grid]');
      const products = this.results.map(product => this.normalizeProduct(product)).filter(product => product && product.available);
      const e = this.escape.bind(this);
      grid.innerHTML = products.length ? products.map(product => {
        const selected = this.items.some(item => item.handle === product.handle);
        const capped = this.items.length >= this.maxProducts;
        const image = e(product.image);
        const price = product.price === null ? this.t('select_variant') : `${this.t('from_price')} ${this.money.format(product.price / 100)}`;
        return `<article class="srs-catalog-card ${selected ? 'is-selected' : ''}"><div class="srs-catalog-card-image"><img src="${image}" alt="${e(product.title)}" width="480" height="360" loading="lazy"></div><div class="srs-catalog-card-body"><h3>${e(product.title)}</h3><div class="srs-catalog-card-price">${e(price)}</div><button type="button" class="srs-button" data-add="${e(product.handle)}" ${selected || capped ? 'disabled' : ''}>${e(selected ? this.t('selected') : capped ? this.t('limit_reached') : this.t('add_product'))}</button></div></article>`;
      }).join('') : `<p class="srs-catalog-empty">${e(this.t('no_products'))}</p>`;
      this.querySelector('[data-dialog-count]').textContent = this.t('selected_count', { count: this.items.length });
    }

    async addProduct(handle, button) {
      if (this.adding || this.items.length >= this.maxProducts || this.items.some(item => item.handle === handle)) return;
      this.adding = true;
      this.productRequest = new AbortController();
      const signal = this.productRequest.signal;
      button.disabled = true;
      button.textContent = this.t('loading_product');
      this.querySelectorAll('[data-add]').forEach(control => { control.disabled = true; });
      try {
        let product = this.products.get(handle);
        if (!product) {
          const response = await fetch(`${this.rootPath}products/${encodeURIComponent(handle)}.js`, { signal, headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Product unavailable');
          product = await response.json();
          this.products.set(handle, product);
        }
        if (signal.aborted || !this.isConnected) return;
        const variants = product.variants?.filter(variant => variant.available) || [];
        const variant = variants[0];
        const image = this.imageUrl(variant?.featured_image || product.featured_image);
        if (!variant || !image) throw new Error('Variant unavailable');
        if (this.items.length >= this.maxProducts || this.items.some(item => item.handle === handle)) return;
        this.items.push({ handle, title: product.title, productId: String(product.id), image, variants, variant, mode: 'replace' });
        this.renderSelection();
        this.renderCatalog();
        this.updateSummary();
        this.querySelector('[data-catalog-status]').textContent = this.t('product_added', { title: product.title });
        this.querySelector('[data-close-catalog]')?.focus();
      } catch (error) {
        if (error.name !== 'AbortError') this.querySelector('[data-catalog-status]').textContent = this.t('product_failed');
        this.renderCatalog();
      } finally {
        this.adding = false;
        if (this.isConnected) this.renderCatalog();
      }
    }

    removeProduct(handle) {
      this.items = this.items.filter(item => item.handle !== handle);
      this.renderSelection();
      this.updateSummary();
      this.status(this.t('product_removed'));
      this.querySelector('[data-open-catalog]').focus();
    }

    renderSelection() {
      const e = this.escape.bind(this);
      this.querySelector('[data-selection-empty]').hidden = Boolean(this.items.length);
      this.querySelector('[data-count]').textContent = `${this.items.length} / ${this.maxProducts}`;
      this.querySelector('[data-selection]').innerHTML = this.items.map((item, index) => {
        const image = this.imageUrl(item.variant.featured_image) || item.image;
        return `<article class="srs-item"><div class="srs-item-top"><img class="srs-item-image" src="${e(image)}" alt="${e(item.title)}" width="70" height="70"><div class="srs-item-info"><h3><span class="srs-item-number">${index + 1}</span>${e(item.title)}</h3><div class="srs-item-price">${e(this.money.format(Number(item.variant.price) / 100))}</div></div><button class="srs-icon-button" type="button" data-remove="${e(item.handle)}" aria-label="${e(this.t('remove_product', { title: item.title }))}"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15"/></svg></button></div><div class="srs-item-fields"><label class="srs-field">${e(this.t('variant'))}<select data-variant data-handle="${e(item.handle)}">${item.variants.map(variant => `<option value="${e(variant.id)}" ${String(variant.id) === String(item.variant.id) ? 'selected' : ''}>${e(variant.title === 'Default Title' ? this.t('default_variant') : variant.title)}</option>`).join('')}</select></label></div></article>`;
      }).join('');
    }

    setBriefMode(mode) {
      if (!['written', 'spoken'].includes(mode) || this.generating || this.dictating || this.dictationTranscribing) return false;
      this.briefMode = mode;
      this.querySelectorAll('[data-brief-mode]').forEach(tab => {
        const selected = tab.dataset.briefMode === mode;
        tab.setAttribute('aria-selected', String(selected));
        tab.setAttribute('tabindex', selected ? '0' : '-1');
      });
      this.querySelectorAll('[data-brief-panel]').forEach(panel => { panel.hidden = panel.dataset.briefPanel !== mode; });
      this.syncBriefPreview();
      return true;
    }

    syncBriefPreview() {
      const preview = this.querySelector('[data-dictation-preview]');
      const value = this.querySelector('[data-brief]')?.value.trim() || '';
      if (preview) { preview.textContent = value; preview.hidden = !value; }
    }

    initDictation() {
      this.dictationSupported = Boolean(window.isSecureContext && navigator.mediaDevices?.getUserMedia && window.MediaRecorder);
      this.dictating = false;
      this.dictationVersion = 0;
      this.updateDictationCountdown(15, false);
      this.querySelector('[data-dictate]').disabled = !this.dictationSupported;
      if (!this.dictationSupported) this.querySelector('[data-dictation-status]').textContent = this.t('dictate_unsupported');
      this.on(this.ownerDocument, 'visibilitychange', () => { if (this.ownerDocument.hidden) this.cancelDictation(); });
      this.on(window, 'pagehide', () => this.cancelDictation());
    }

    async toggleDictation() {
      if (this.generating || !this.dictationSupported || this.dictationTranscribing) return;
      if (this.dictating) { this.stopDictation(); return; }
      const input = this.querySelector('[data-brief]');
      if (input.value.length >= 400) { this.querySelector('[data-dictation-status]').textContent = this.t('dictate_limit'); return; }
      const version = ++this.dictationVersion;
      this.dictating = true;
      this.dictationBase = input.value.trimEnd();
      input.readOnly = true;
      this.querySelector('[data-dictate]').disabled = true;
      this.querySelector('[data-dictation-status]').textContent = this.t('dictate_starting');
      this.updateSummary();
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true }, video: false });
        if (version !== this.dictationVersion || !this.isConnected) { stream.getTracks().forEach(track => track.stop()); return; }
        this.dictationStream = stream;
        const mime = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm'].find(type => window.MediaRecorder.isTypeSupported(type));
        if (!mime) throw new Error('Unsupported recorder');
        const recorder = this.recorder = new window.MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 64000 });
        const chunks = [];
        recorder.ondataavailable = event => { if (event.data.size) chunks.push(event.data); };
        recorder.onerror = () => { this.cancelDictation(); this.querySelector('[data-dictation-status]').textContent = this.t('dictate_failed'); };
        recorder.onstop = async () => {
          stream.getTracks().forEach(track => track.stop());
          if (version !== this.dictationVersion) return;
          clearInterval(this.dictationCountdown);
          clearTimeout(this.dictationTimer);
          this.recorder = null;
          this.dictationTranscribing = true;
          this.updateDictationCountdown(0, false);
          this.querySelector('[data-dictate]').disabled = true;
          this.querySelector('[data-dictation-status]').textContent = this.t('dictate_transcribing');
          try {
            const audio = new Blob(chunks, { type: mime.split(';')[0] });
            if (!audio.size || audio.size > 1024 * 1024) throw new Error('Invalid recording');
            const form = new FormData();
            form.set('audio', audio, 'dictation'); form.set('visitor_id', this.visitorId); form.set('consent', 'true');
            this.dictationRequest = new AbortController();
            const timeout = setTimeout(() => this.dictationRequest?.abort(), 30000);
            let response;
            try { response = await fetch(`${this.proxyBase}/dictation`, { method: 'POST', body: form, headers: { Accept: 'application/json' }, signal: this.dictationRequest.signal }); }
            finally { clearTimeout(timeout); }
            const data = await response.json();
            if (version !== this.dictationVersion) return;
            if (!response.ok || !data.ok) { this.finishDictation(data.error?.startsWith('PROMPT_') ? this.errorMessage(data.error) : this.t(data.error === 'DICTATION_LIMIT' ? 'dictate_rate_limit' : 'dictate_network')); return; }
            if (typeof data.transcript !== 'string') throw new Error('Invalid transcript');
            input.value = [this.dictationBase, data.transcript.trim()].filter(Boolean).join(' ').slice(0,400);
            this.finishDictation(this.t(input.value.length >= 400 ? 'dictate_limit' : data.transcript.trim() ? 'dictate_done' : 'dictate_no_speech'));
          } catch { if (version === this.dictationVersion) this.finishDictation(this.t('dictate_network')); }
        };
        recorder.start();
        this.querySelector('[data-dictate]').disabled = false;
        this.querySelector('[data-dictate]').setAttribute('aria-pressed', 'true');
        this.querySelector('[data-dictate-label]').textContent = this.t('dictate_stop');
        const deadline = Date.now() + 15000;
        const tick = () => {
          const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
          this.updateDictationCountdown(remaining, true);
          this.querySelector('[data-dictation-status]').textContent = this.t('dictate_listening', { seconds: remaining });
        };
        tick();
        this.dictationCountdown = setInterval(tick, 250);
        this.dictationTimer = setTimeout(() => this.stopDictation(), 15000);
      } catch (error) {
        if (version !== this.dictationVersion) return;
        this.cancelDictation();
        this.querySelector('[data-dictation-status]').textContent = this.t(['NotAllowedError','SecurityError'].includes(error.name) ? 'dictate_denied' : error.name === 'NotFoundError' ? 'dictate_no_microphone' : 'dictate_failed');
      }
    }

    updateDictationCountdown(seconds, recording) {
      const counter = this.querySelector('[data-dictation-countdown]');
      if (!counter) return;
      counter.textContent = `${seconds}″`;
      counter.setAttribute('data-recording', String(recording));
      counter.hidden = !recording;
    }

    stopDictation() {
      if (!this.recorder || this.recorder.state !== 'recording') return;
      clearTimeout(this.dictationTimer);
      clearInterval(this.dictationCountdown);
      this.querySelector('[data-dictate]').disabled = true;
      this.querySelector('[data-dictation-status]').textContent = this.t('dictate_transcribing');
      this.updateDictationCountdown(0, false);
      this.recorder.stop();
      this.dictationStream?.getTracks().forEach(track => track.stop());
    }

    finishDictation(message = '') {
      clearTimeout(this.dictationTimer); clearInterval(this.dictationCountdown);
      this.dictationStream?.getTracks().forEach(track => track.stop());
      this.dictationStream = null; this.recorder = null;
      this.dictating = false; this.dictationTranscribing = false;
      this.updateDictationCountdown(15, false);
      const input = this.querySelector('[data-brief]'); if (input) input.readOnly = false;
      const button = this.querySelector('[data-dictate]');
      if (button) { button.disabled = Boolean(this.generating || !this.dictationSupported); button.setAttribute('aria-pressed', 'false'); }
      const label = this.querySelector('[data-dictate-label]'); if (label) label.textContent = this.t('dictate_start');
      const status = this.querySelector('[data-dictation-status]'); if (status) status.textContent = message;
      if (this.initialized) this.updateSummary();
      this.syncBriefPreview();
    }

    cancelDictation() {
      this.dictationVersion++;
      this.dictationRequest?.abort();
      const recorder = this.recorder;
      if (recorder) { recorder.onstop = recorder.ondataavailable = recorder.onerror = null; if (recorder.state === 'recording') recorder.stop(); }
      this.finishDictation();
    }

    compositionInstruction() { return this.querySelector('[data-brief]').value.trim().slice(0, 400); }

    updateSummary() {
      this.syncBriefPreview();
      this.querySelectorAll('[data-brief-mode]').forEach(tab => { tab.disabled = Boolean(this.generating || this.dictating || this.dictationTranscribing); });
      const subtotal = this.items.reduce((sum, item) => sum + Number(item.variant.price), 0);
      this.querySelector('[data-subtotal]').textContent = this.items.length ? this.money.format(subtotal / 100) : '—';
      this.querySelector('[data-summary]').textContent = this.items.length ? this.t('summary_count', { count: this.items.length }) : this.t('summary_empty');
      const instruction = this.compositionInstruction();
      this.querySelector('[data-brief-count]').textContent = `${this.querySelector('[data-brief]').value.length} / 400`;
      const stage = !this.photoUrl ? 'photo' : !this.items.length ? 'products' : 'review';
      const steps = ['photo', 'products', 'review'];
      this.querySelectorAll('[data-step]').forEach(step => {
        step.classList.toggle('is-complete', steps.indexOf(step.dataset.step) < steps.indexOf(stage));
        if (step.dataset.step === stage) step.setAttribute('aria-current', 'step');
        else step.removeAttribute('aria-current');
      });
      const ready = Boolean(this.serviceReady && this.roomFile && this.items.length && instruction.length >= 10 && this.querySelector('[data-consent]').checked && !this.generating && !this.uncertain && !this.dictating);
      this.querySelector('[data-generate]').disabled = !ready;
      this.querySelector('[data-generate-label]').textContent = this.t(this.generating ? 'generating' : 'generate');
    }

    async connectService(checkRequest = false) {
      const retry = this.querySelector('[data-retry-connection]');
      retry.hidden = true;
      try {
        const url = new URL(`${this.proxyBase}/composition`, location.origin);
        url.searchParams.set('visitor_id', this.visitorId);
        if (checkRequest && this.requestId) url.searchParams.set('request_id', this.requestId);
        const response = await fetch(url, { headers: { Accept: 'application/json', 'x-room-email-token': this.emailToken }, signal: AbortSignal.timeout(20000) });
        const data = await response.json();
        if (!response.ok || !data.enabled) throw new Error('Service unavailable');
        this.quota = data.quota;
        this.serviceReady = Boolean(data.quota?.allowed);
        this.querySelector('[data-service-status]').textContent = this.t('quota_remaining', { count: data.quota?.remaining ?? 0 });
        if (data.turnstileSiteKey) await this.initTurnstile(data.turnstileSiteKey);
        if (checkRequest && data.request) {
          if (['RESERVED', 'UNKNOWN'].includes(data.request.status)) {
            this.uncertain = true;
            this.status(this.t('request_pending'));
            retry.hidden = false;
          } else {
            this.uncertain = false;
            this.requestId = '';
            this.status(this.t(data.request.status === 'COMPLETE' ? 'result_connection_lost' : 'generation_failed'));
          }
        } else if (!this.serviceReady) this.status(this.t('quota_reached'));
      } catch {
        this.serviceReady = false;
        this.querySelector('[data-service-status]').textContent = this.t('connection_failed');
        retry.hidden = false;
      }
      this.updateSummary();
    }

    async initTurnstile(key) {
      if (this.turnstileWidget !== undefined) return;
      if (!window.turnstile) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
          script.onload = resolve;
          script.onerror = reject;
          document.head.append(script);
        });
      }
      this.turnstileWidget = window.turnstile.render(this.querySelector('[data-turnstile]'), { sitekey: key, callback: token => { this.turnstileToken = token; } });
    }

    async roomForRequest() {
      const image = new Image();
      image.src = this.photoUrl;
      await image.decode();
      const scale = Math.min(1, 1800 / Math.max(image.naturalWidth, image.naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
      const context = canvas.getContext('2d');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', .9));
      if (!blob || blob.size > 6 * 1024 * 1024) throw new Error('Invalid photo');
      return new File([blob], 'room.jpg', { type: 'image/jpeg' });
    }

    setGenerating(value) {
      if (value) this.cancelDictation();
      this.generating = value;
      this.classList.toggle('is-generating', value);
      this.setAttribute('aria-busy', String(value));
      const loading = this.querySelector('[data-generating-panel]');
      if (loading) { loading.hidden = !value; if (value) loading.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
      this.querySelectorAll('[data-selection] input,[data-selection] select,[data-selection] button,[data-open-catalog],[data-choose-photo],[data-replace-photo],[data-clear-photo],[data-take-photo],[data-consent],[data-brief]').forEach(control => { control.disabled = value; });
      const dictate = this.querySelector('[data-dictate]');
      if (dictate) dictate.disabled = value || !this.dictationSupported;
      this.updateSummary();
    }

    errorMessage(code, index) {
      const key = ({ CONSENT_REQUIRED: 'need_consent', QUOTA_REACHED: 'quota_reached', INVALID_ROOM: 'invalid_photo', INVALID_ITEMS: 'need_products', INVALID_ITEM: 'need_placement', PRODUCT_NOT_ELIGIBLE: 'product_failed', VARIANT_IMAGE_REQUIRED: 'variant_image_required', CATALOG_UNAVAILABLE: 'product_failed', CATALOG_IMAGE_UNAVAILABLE: 'product_failed', BOT_CHECK_FAILED: 'bot_check', AI_CREDITS_REQUIRED: 'credits_unavailable', GENERATION_TIMEOUT: 'generation_timeout', REQUEST_ALREADY_SUBMITTED: 'request_pending', REQUEST_CONFLICT: 'request_pending', IMAGE_HUMAN_CONTENT: 'image_human_content', IMAGE_OUT_OF_SCOPE: 'image_out_of_scope', RESULT_SCOPE_BLOCKED: 'result_scope_blocked', PROMPT_UNSAFE: 'prompt_unsafe', PROMPT_OUT_OF_SCOPE: 'prompt_out_of_scope', PROMPT_UNSELECTED_PRODUCT: 'prompt_unselected_product', PROMPT_REVIEW_UNAVAILABLE: 'prompt_review_unavailable', SETUP_REQUIRED: 'connection_failed' })[code] || 'generation_failed';
      return this.t(key) + (Number.isInteger(index) ? ` (${index + 1})` : '');
    }

    async generate() {
      if (this.generating || this.uncertain) return;
      if (this.dictating) { this.querySelector('[data-dictation-status]').textContent = this.t('dictate_finish_first'); return; }
      if (!this.roomFile) { this.status(this.t('need_photo')); return; }
      if (!this.items.length) { this.status(this.t('need_products')); return; }
      const instruction = this.compositionInstruction();
      if (instruction.length < 10) { this.status(this.t('need_placement')); this.setBriefMode('written'); this.querySelector('[data-brief]')?.focus(); return; }
      if (!this.querySelector('[data-consent]').checked) { this.status(this.t('need_consent')); return; }
      if (this.turnstileWidget !== undefined && !this.turnstileToken) { this.status(this.t('bot_check')); return; }
      this.setGenerating(true);
      this.status(this.t('generating_note'));
      const submittedPhoto = this.photoUrl;
      const items = this.items.map(item => ({ product_handle: item.handle, variant_id: String(item.variant.id), mode: 'replace', instruction }));
      let sent = false;
      this.requestId = crypto.randomUUID();
      try {
        const form = new FormData();
        form.set('room', await this.roomForRequest());
        form.set('visitor_id', this.visitorId);
        form.set('email_token', this.emailToken);
        form.set('consent', 'true');
        form.set('client_request_id', this.requestId);
        form.set('items_json', JSON.stringify(items));
        if (this.turnstileToken) form.set('turnstile_token', this.turnstileToken);
        sent = true;
        const response = await fetch(`${this.proxyBase}/composition`, { method: 'POST', body: form, headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(240000) });
        const data = await response.json();
        if (!response.ok || !data.ok) {
          this.uncertain = ['GENERATION_TIMEOUT', 'REQUEST_ALREADY_SUBMITTED', 'REQUEST_CONFLICT'].includes(data.error);
          this.status(this.errorMessage(data.error, data.itemIndex));
          if (this.uncertain) this.querySelector('[data-retry-connection]').hidden = false;
          else this.requestId = '';
          if (data.quota) { this.quota = data.quota; this.serviceReady = Boolean(data.quota.allowed); }
          return;
        }
        if (!/^data:image\/(png|jpeg|webp);base64,/.test(data.preview) || !Array.isArray(data.items) || data.items.length !== items.length) throw new Error('Invalid result');
        this.requestId = '';
        this.result = { image: data.preview, items: data.items, previewId: data.previewId };
        if (this.resultPhotoUrl && this.resultPhotoUrl !== submittedPhoto) URL.revokeObjectURL(this.resultPhotoUrl);
        this.resultPhotoUrl = submittedPhoto;
        this.showResult();
        this.status(this.t('generation_complete'));
        this.quota = data.quota;
        this.serviceReady = Boolean(data.quota?.allowed);
        this.querySelector('[data-service-status]').textContent = this.t('quota_remaining', { count: data.quota?.remaining ?? 0 });
        window.StamatisStudioAnalytics?.track('generation_complete', data.items.map(item=>item.handle));
      } catch {
        this.uncertain = sent;
        this.status(this.t(sent ? 'network_uncertain' : 'invalid_photo'));
        if (sent) this.querySelector('[data-retry-connection]').hidden = false;
      } finally {
        if (this.turnstileWidget !== undefined) { this.turnstileToken = ''; window.turnstile?.reset(this.turnstileWidget); }
        this.setGenerating(false);
      }
    }

    showResult() {
      const section = this.querySelector('[data-result]');
      this.querySelector('[data-compare]').value = '50';
      this.querySelector('[data-comparison]').style.setProperty('--split','50%');
      const original = this.querySelector('[data-result-original]');
      original.src = this.resultPhotoUrl;
      original.onload = () => this.querySelector('[data-comparison]').style.setProperty('--result-ratio', `${original.naturalWidth} / ${original.naturalHeight}`);
      this.querySelector('[data-result-generated]').src = this.result.image;
      this.querySelector('[data-result-items]').innerHTML = this.result.items.map(item => `<a class="srs-result-product" href="${this.rootPath}products/${encodeURIComponent(item.handle)}?variant=${encodeURIComponent(item.variantId)}" target="_blank" rel="noopener"><img src="${this.escape(this.imageUrl(item.imageUrl))}" width="60" height="60" alt=""><span>${this.escape(item.title)}<small>${this.escape(item.variantTitle === 'Default Title' ? this.t('default_variant') : item.variantTitle)}</small></span><span aria-hidden="true">↗</span></a>`).join('');
      section.hidden = false;
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    async downloadResult() {
      if (!this.result?.image) return;
      const image = new Image();
      image.src = this.result.image;
      await image.decode();
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const context = canvas.getContext('2d');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0);
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', .95));
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const date = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Athens' }).format(new Date()).replaceAll('/', '-');
      link.download = `stamatis-center-${date}-composition.JPEG`;
      link.click();
      window.StamatisStudioAnalytics?.track('download', this.result.items.map(item=>item.handle));
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    }
  }

  customElements.define('stamatis-room-studio', StamatisRoomStudio);
})();
